// Ticket helper functions — ported from casi-mobile/lib/cases/ticket_helpers.dart
// Maps priority, status, issue type, and SLA values to colors, labels, and icons.

import {
  SVGIcon,
  warningTriangleIcon,
  eyeIcon,
  userIcon,
  lockIcon,
  globeIcon,
  exclamationCircleIcon,
} from '@progress/kendo-svg-icons';

// ---------------------------------------------------------------------------
// Priority colors (maps to Bootstrap/project CSS variables)
// ---------------------------------------------------------------------------

export function getPriorityColor(priority: string): string {
  switch (priority.toLowerCase()) {
    case 'critical':
      return 'var(--bs-danger)';
    case 'high':
      return 'var(--bs-warning)';
    case 'medium':
      return 'var(--cyflare-purple, #7c3aed)';
    case 'low':
      return 'var(--bs-info)';
    default:
      return 'var(--bs-info)';
  }
}

export function getPriorityBadgeClass(priority: string): string {
  switch (priority.toLowerCase()) {
    case 'critical':
      return 'bg-danger';
    case 'high':
      return 'bg-warning text-dark';
    case 'medium':
      return 'bg-purple';
    case 'low':
      return 'bg-info';
    default:
      return 'bg-secondary';
  }
}

// ---------------------------------------------------------------------------
// Status colors
// ---------------------------------------------------------------------------

export function getStatusColor(status: string | null | undefined): string {
  if (!status) return 'var(--bs-secondary)';
  const s = status.toLowerCase();

  if (s.includes('awaiting response from client')) return 'var(--bs-warning)';
  if (s.includes('awaiting soc') || s.includes('awaiting customer')) return 'var(--bs-info)';
  if (s.startsWith('tp -') || s.includes('closed') || s.includes('resolved')) return 'var(--bs-success)';
  if (s.includes('open') || s.includes('new')) return 'var(--bs-primary)';

  return 'var(--bs-secondary)';
}

export function getStatusBadgeClass(status: string | null | undefined): string {
  if (!status) return 'bg-secondary';
  const s = status.toLowerCase();

  if (s.includes('awaiting response from client')) return 'bg-warning text-dark';
  if (s.includes('awaiting soc') || s.includes('awaiting customer')) return 'bg-info';
  if (s.startsWith('tp -') || s.includes('closed') || s.includes('resolved')) return 'bg-success';
  if (s.includes('open') || s.includes('new')) return 'bg-primary';

  return 'bg-secondary';
}

// ---------------------------------------------------------------------------
// Status label shortener
// ---------------------------------------------------------------------------

export function shortenStatusLabel(status: string | null | undefined): string {
  if (!status) return 'Unknown';
  const s = status.toLowerCase();

  if (s.includes('awaiting response from client')) return 'Awaiting Client';
  if (s.includes('awaiting soc reply')) return 'Awaiting SOC';
  if (s.includes('awaiting customer')) return 'Awaiting Customer';
  if (s === 'tp - no action needed') return 'No Action Needed';
  if (s === 'tp - solution tuning implemented') return 'Tuning Applied';
  if (s.startsWith('tp -')) return status.substring(5).trim();
  if (s.includes('closed')) return 'Closed';
  if (s.includes('resolved')) return 'Resolved';
  if (s.includes('open')) return 'Open';

  return status.length > 24 ? status.substring(0, 22) + '…' : status;
}

// ---------------------------------------------------------------------------
// Issue type icons (using Kendo SVG icons)
// ---------------------------------------------------------------------------

export function getIssueTypeIcon(issueType: string): SVGIcon {
  const t = issueType.toLowerCase();

  if (t.includes('malware')) return warningTriangleIcon;
  if (t.includes('malicious connection')) return globeIcon;
  if (t.includes('suspicious')) return eyeIcon;
  if (t.includes('credential') || t.includes('user compromise')) return userIcon;
  if (t.includes('phishing')) return exclamationCircleIcon;
  if (t.includes('brute') || t.includes('login')) return lockIcon;

  return lockIcon;
}

// ---------------------------------------------------------------------------
// SLA violation
// ---------------------------------------------------------------------------

export function getSlaColor(slaViolationType: string): string {
  if (slaViolationType.toLowerCase().includes('not violated')) {
    return 'var(--bs-success)';
  }
  return 'var(--bs-danger)';
}

export function getSlaBadgeClass(slaViolationType: string): string {
  if (slaViolationType.toLowerCase().includes('not violated')) {
    return 'bg-success';
  }
  return 'bg-danger';
}

// ---------------------------------------------------------------------------
// Relative time formatting
// ---------------------------------------------------------------------------

export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Response action icon mapping
// ---------------------------------------------------------------------------

export function getResponseActionIcon(actionType: string): SVGIcon {
  switch (actionType) {
    case 'block_ip':
      return exclamationCircleIcon;
    case 'isolate_endpoint':
      return globeIcon;
    case 'disable_user':
    case 'force_password_reset':
      return userIcon;
    case 'block_domain':
      return globeIcon;
    default:
      return lockIcon;
  }
}

export function getResponseActionStatusClass(status: string): string {
  switch (status) {
    case 'available':
      return 'text-primary';
    case 'pending':
      return 'text-warning';
    case 'completed':
      return 'text-success';
    case 'failed':
      return 'text-danger';
    default:
      return 'text-secondary';
  }
}
